import { getCombinedId } from 'utils/helpers'
import { getActiveIntegrationLabel } from 'utils/integrations'
import { getPlanName } from 'utils/plans'
import { capturePosthogEvent, groupPosthogCompany } from 'utils/posthog'
import { buildURL } from 'utils/urls'

import {
  selectActualBrandName,
  selectActualCompanyId,
  selectActualCompanyName,
  selectActualIntegrations,
  selectActualIsPartner,
  selectActualLicenses,
  selectActualMailboxEnabled,
  selectActualMailboxLimit,
  selectActualPartnerName,
  selectEnvironment,
  selectIsIneligibleTriggerBasedCampaignsAccount,
  selectRegion,
} from 'containers/App/selectors'
import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import { MAP_INCIDENT_TYPE_TO_NAME } from 'containers/ResolutionCenter/constants'

const THEME_STORAGE_KEY = 'ironscales-theme'
export const CLASSIC_DASHBOARD_KEY = 'home_dashboard_classic'

const VALID_THEMES = ['light', 'dark', 'system']

function getStoredThemePreference() {
  if (typeof window === 'undefined') return 'light'
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    return VALID_THEMES.includes(stored) ? stored : 'light'
  } catch {
    return 'light'
  }
}

function getEffectiveDarkMode(themePreference) {
  if (themePreference === 'dark') return true
  if (themePreference === 'light') return false
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function getStoredClassicPreference() {
  if (typeof window === 'undefined') return false
  try {
    return JSON.parse(localStorage.getItem(CLASSIC_DASHBOARD_KEY)) === true
  } catch {
    return false
  }
}

/**
 * @param {{ themePreference?: string, effectiveTheme?: string, classicDashboard?: boolean }} [overrides]
 */
export function getPendoVisitorPreferences(overrides = {}) {
  const themePreference = overrides.themePreference ?? getStoredThemePreference()
  const darkModeEnabled =
    overrides.effectiveTheme !== undefined
      ? overrides.effectiveTheme === 'dark'
      : getEffectiveDarkMode(themePreference)

  const classicDashboard =
    overrides.classicDashboard !== undefined
      ? overrides.classicDashboard
      : getStoredClassicPreference()

  return {
    theme_preference: themePreference,
    dark_mode_enabled: darkModeEnabled,
    dashboard_view: classicDashboard ? 'classic' : 'narrative',
  }
}

/**
 * @param {{ themePreference?: string, effectiveTheme?: string, classicDashboard?: boolean }} [overrides]
 */
export function syncPendoVisitorPreferences(overrides = {}) {
  if (!window.pendo?.updateOptions || !window.IRONSCALES?.pendoData) return

  window.IRONSCALES.pendoData.visitor = {
    ...window.IRONSCALES.pendoData.visitor,
    ...getPendoVisitorPreferences(overrides),
  }
  window.pendo.updateOptions(window.IRONSCALES.pendoData)
}

export const pendoMSPSupportMiddleware = (store) => (next) => (action) => {
  const result = next(action) // reach the reducer first
  if (action.type !== FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS) return result

  const state = store.getState()
  const account = {
    id: getCombinedId(selectEnvironment(state), selectRegion(state), selectActualCompanyId(state)),
    company_name: selectActualCompanyName(state),
    integration: getActiveIntegrationLabel(selectActualIntegrations(state)),
    plan: getPlanName(selectActualLicenses(state).plan),
    is_partner: selectActualIsPartner(state),
    brand_name: selectActualBrandName(state),
    partner_name: selectActualPartnerName(state),
    mailbox_limit: selectActualMailboxLimit(state),
    mailbox_enabled: selectActualMailboxEnabled(state),
    trigger_based_campaigns_enabled: !selectIsIneligibleTriggerBasedCampaignsAccount(state),
  }

  const { id: companyId, ...accountProperties } = account
  groupPosthogCompany(companyId, { name: account.company_name, ...accountProperties })

  if (typeof window.pendo?.initialize === 'function' && window.IRONSCALES?.pendoData) {
    window.IRONSCALES.pendoData.account = account
    syncPendoVisitorPreferences()
  }

  return result
}

export const pendoAttachIncident = (reportId, reportType) => {
  if (window.pendo && window.pendo.location?.setUrl) {
    window.pendo.location.setUrl(
      buildURL(window.location.href, {
        reportId,
        reportType: MAP_INCIDENT_TYPE_TO_NAME[reportType],
      })
    )
  }
}

export const pendoDetachIncident = () => {
  if (window.pendo && window.pendo.location?.useBrowserUrl) {
    window.pendo.location.useBrowserUrl()
  }
}

/**
 * Fire a Pendo Track Event and dual-write the same payload to PostHog.
 * Each sink no-ops independently when that agent is unavailable (local/dev/on-prem).
 * @param {string} eventName
 * @param {Record<string, string | number | boolean>} [properties]
 */
export function trackPendoEvent(eventName, properties = {}) {
  if (!eventName) return

  if (typeof window.pendo?.track === 'function') {
    try {
      window.pendo.track(eventName, properties)
    } catch {
      // Pendo agent can throw before initialize completes; never break product flows.
    }
  }

  capturePosthogEvent(eventName, properties)
}
